package diana.soleil.design.iraniancanadienbazzar.feature.marketplace

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BlockRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.DealsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.FeatureFlags
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Deal
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flowOf
import diana.soleil.design.iraniancanadienbazzar.core.data.ChatRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.ImagePickController
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.core.data.MarketplaceRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.SavedItemsController
import kotlinx.coroutines.flow.mapLatest
import diana.soleil.design.iraniancanadienbazzar.core.model.Country
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemCondition
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceItem
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

enum class MarketplaceSort { Newest, PriceLow, PriceHigh }

/** Client-side price filter bands on the browse tab (free items count as $0). */
enum class MarketplacePriceBand { Any, Free, Under50, From50To200, Over200 }

data class MarketplaceUiState(
    val loading: Boolean = true,
    val items: List<MarketplaceItem> = emptyList(),
    /** The signed-in user's own listings (any status), shown as a "Your listings" section. */
    val myItems: List<MarketplaceItem> = emptyList(),
    /** Bookmarked listings resolved from [savedIds], shown as a "Saved" section. */
    val savedItems: List<MarketplaceItem> = emptyList(),
    /** Ids of bookmarked listings (for the heart state on browse cards). */
    val savedIds: Set<String> = emptySet(),
    val condition: ItemCondition? = null,
    val category: MarketplaceCategory? = null,
    val sort: MarketplaceSort = MarketplaceSort.Newest,
    /** Free-text search over title + description (client-side, case-insensitive). */
    val searchText: String = "",
    val priceBand: MarketplacePriceBand = MarketplacePriceBand.Any,
    val error: Boolean = false,
    /** Create-listing sub-state. */
    val publishing: Boolean = false,
    /** Guest tried to publish — the route routes them to sign-in. */
    val signInRequired: Boolean = false,
    val publishFailed: Boolean = false,
    /** Flips true once after a successful publish so the sheet can close + confirm. */
    val justPublished: Boolean = false,
    /** The listing published but one or more of its photos failed to upload — warn the seller. */
    val photoUploadWarning: Boolean = false,
    /** Live merchant promotions for the Hot Deals block (Stitch 66b7e10b), soonest-ending first. */
    val deals: List<Deal> = emptyList(),
    /** The businesses behind [deals], keyed by id — supplies the hero cover/logo and verified state. */
    val dealBusinesses: Map<String, Business> = emptyMap(),
    /** Retail/grocery listings of the active metro — the "shops in your mahaleh" rail. */
    val shops: List<Business> = emptyList(),
)

@OptIn(ExperimentalCoroutinesApi::class)
class MarketplaceViewModel(
    private val repository: MarketplaceRepository,
    private val cityController: CityController,
    private val savedController: SavedItemsController,
    private val imagePicker: ImagePickController,
    blockRepository: BlockRepository,
    dealsRepository: DealsRepository,
    private val businessRepository: BusinessRepository,
    featureFlags: FeatureFlags,
) : ViewModel() {
    /** Active country (from the city switcher) so city autocomplete only suggests in-country places. */
    val activeCountry: StateFlow<Country> = cityController.activeCountry

    private val retryTrigger = MutableStateFlow(0)
    private val condition = MutableStateFlow<ItemCondition?>(null)
    private val category = MutableStateFlow<MarketplaceCategory?>(null)
    private val sort = MutableStateFlow(MarketplaceSort.Newest)
    private val searchText = MutableStateFlow("")
    private val priceBand = MutableStateFlow(MarketplacePriceBand.Any)
    private val publishing = MutableStateFlow(false)
    private val publishGate = MutableStateFlow<AppError?>(null)
    private val published = MutableStateFlow(false)
    private val photoUploadFailed = MutableStateFlow(false)

    private val items: Flow<DataResult<List<MarketplaceItem>>> =
        combine(cityController.activeMetroId, retryTrigger) { metroId, _ -> metroId }
            .flatMapLatest(repository::observeActiveItems)

    // Browse list with blocked sellers dropped (same client-side block filter as the feed).
    private val visibleItems: Flow<DataResult<List<MarketplaceItem>>> =
        combine(items, blockRepository.observeBlockedUids()) { result, blocked ->
            when (result) {
                is DataResult.Success -> DataResult.Success(result.data.filter { it.sellerUid !in blocked })
                is DataResult.Failure -> result
            }
        }

    // All client-side browse filters bundled so the main combine stays within its arity limit.
    private val filters: Flow<MarketplaceFilters> =
        combine(condition, category, sort, searchText, priceBand) { c, cat, s, q, band -> MarketplaceFilters(c, cat, s, q, band) }

    // Hot Deals are additive (like the Home rail): flag-off or a failure just hides the block.
    private val hotDeals: Flow<List<Deal>> = featureFlags.available.flatMapLatest { flags ->
        if (!flags.deals) flowOf(emptyList())
        else dealsRepository.observeActiveDeals(HOT_DEALS_COUNT)
            .map { (it as? DataResult.Success)?.data ?: emptyList() }
            .catch { emit(emptyList()) }
    }

    // The hero needs the merchant's cover/logo, which the deal document doesn't carry.
    private val hotDealsWithBusinesses: Flow<Pair<List<Deal>, Map<String, Business>>> =
        hotDeals.mapLatest { deals ->
            val businesses = if (deals.isEmpty()) emptyList()
            else runCatching { businessRepository.getBusinesses(deals.map { it.businessId }.distinct()) }.getOrDefault(emptyList())
            deals to businesses.associateBy { it.id }
        }

    // Retail + grocery listings of the active metro. The peer market starts empty in a new city; the
    // directory's shops are real content to browse in the meantime (and stay useful once it fills).
    private val shops: Flow<List<Business>> =
        combine(cityController.activeMetroId, cityController.activeCountry) { metro, country -> metro to country }
            .flatMapLatest { (metro, country) ->
                combine(
                    SHOP_CATEGORY_IDS.map { categoryId ->
                        businessRepository.observeBusinesses(metro, categoryId, country.code)
                            .map { (it as? DataResult.Success)?.data ?: emptyList() }
                            .catch { emit(emptyList()) }
                    },
                ) { lists ->
                    lists.flatMap { it }
                        .sortedWith(
                            compareByDescending<Business> { it.verified }
                                .thenByDescending { it.rating }
                                .thenByDescending { it.reviewCount },
                        )
                        .take(SHOPS_RAIL_COUNT)
                }
            }

    private data class Discover(val deals: List<Deal>, val dealBusinesses: Map<String, Business>, val shops: List<Business>)

    private val discover: Flow<Discover> =
        combine(hotDealsWithBusinesses, shops) { (deals, businesses), shops -> Discover(deals, businesses, shops) }

    private val listState: Flow<Triple<DataResult<List<MarketplaceItem>>, MarketplaceFilters, Discover>> =
        combine(visibleItems, filters, discover) { result, f, d -> Triple(result, f, d) }

    private val publishState: Flow<PublishState> =
        combine(publishing, publishGate, published, photoUploadFailed) { p, g, done, warn ->
            PublishState(p, g, done, warn)
        }

    // Resolve saved ids → items (best-effort; cross-metro, so it can't just filter the active list).
    // IDS flow separately from the resolved ITEMS: the ids must reach the UI instantly so the heart
    // toggles on tap — routing them through this network mapLatest made rapid taps cancel each
    // other and the heart looked dead until the refetch finished (owner device report 2026-07-24).
    private val savedItemsResolved: Flow<List<MarketplaceItem>> =
        savedController.saved.mapLatest { ids ->
            if (ids.isEmpty()) emptyList() else repository.getItems(ids)
        }

    val state: StateFlow<MarketplaceUiState> =
        combine(listState, publishState, repository.observeMyItems(), savedController.saved, savedItemsResolved) {
                (result, f, discover), ps, myResult, savedIdsNow, savedItemsNow ->
            val isPublishing = ps.publishing
            val gate = ps.gate
            val done = ps.done
            val base = when (result) {
                is DataResult.Success -> MarketplaceUiState(
                    loading = false,
                    items = result.data
                        .filter { f.condition == null || it.condition == f.condition }
                        .filter { f.category == null || it.category == f.category }
                        .filter { it.matchesSearch(f.search) }
                        .filter { it.matchesPriceBand(f.priceBand) }
                        .sortedWith(f.sort.comparator),
                    condition = f.condition,
                    category = f.category,
                    sort = f.sort,
                    searchText = f.search,
                    priceBand = f.priceBand,
                )
                is DataResult.Failure -> MarketplaceUiState(
                    loading = false,
                    condition = f.condition,
                    category = f.category,
                    sort = f.sort,
                    searchText = f.search,
                    priceBand = f.priceBand,
                    error = true,
                )
            }
            base.copy(
                myItems = (myResult as? DataResult.Success)?.data.orEmpty(),
                savedIds = savedIdsNow,
                savedItems = savedItemsNow,
                publishing = isPublishing,
                signInRequired = gate is AppError.Unauthorized,
                publishFailed = gate != null && gate !is AppError.Unauthorized,
                justPublished = done,
                photoUploadWarning = ps.photoWarning,
                deals = discover.deals,
                dealBusinesses = discover.dealBusinesses,
                shops = discover.shops,
            )
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), MarketplaceUiState())

    fun toggleSaved(itemId: String) = savedController.toggle(itemId)

    fun setCondition(value: ItemCondition?) {
        condition.value = value
    }

    fun setCategory(value: MarketplaceCategory?) {
        category.value = value
    }

    fun setSort(value: MarketplaceSort) {
        sort.value = value
    }

    fun setSearchText(value: String) {
        searchText.value = value
    }

    fun setPriceBand(value: MarketplacePriceBand) {
        priceBand.value = value
    }

    fun retry() {
        retryTrigger.value += 1
    }

    /**
     * Publishes a listing to the active metro. [priceCents] is ignored when [isFree]; [city] is the
     * seller's municipality. Reading is public, but publishing needs a real account — an anonymous
     * caller trips [MarketplaceUiState.signInRequired] via the repository's `Unauthorized`.
     */
    /** Opens the system picker for the sell form to buffer a photo before the listing exists. */
    suspend fun pickPhoto() = imagePicker.pick()

    fun publish(
        title: String,
        description: String,
        priceCents: Long?,
        isFree: Boolean,
        condition: ItemCondition,
        category: MarketplaceCategory,
        city: String,
        photos: List<PickedImage>,
    ) {
        if (title.isBlank() || publishing.value) return
        viewModelScope.launch {
            publishing.value = true
            publishGate.value = null
            photoUploadFailed.value = false
            val result = repository.createItem(
                title = title,
                description = description,
                priceCents = if (isFree) null else priceCents,
                isFree = isFree,
                condition = condition,
                category = category,
                metroId = cityController.activeMetroId.value,
                city = city,
                photoUrls = emptyList(),
            )
            when (result) {
                is DataResult.Success -> {
                    // Photos need the doc to exist first — upload the buffered picks in order (first =
                    // main). Best-effort: a failed photo doesn't fail the listing (the seller can add
                    // photos later from the item detail); the listing itself already published. But we
                    // COUNT failures so the seller is warned instead of silently losing photos under a
                    // plain "listing is live" (QA 2026-07-29).
                    val anyPhotoFailed = photos.take(8)
                        .map { repository.addPhoto(result.data, it) }
                        .any { it is DataResult.Failure }
                    photoUploadFailed.value = anyPhotoFailed
                    published.value = true
                }
                is DataResult.Failure -> publishGate.value = result.error
            }
            publishing.value = false
        }
    }

    /** Clears the sign-in / failure gate after the route has reacted to it. */
    fun consumePublishGate() {
        publishGate.value = null
    }

    /** Clears the one-shot success flag (and its photo-upload warning) after the sheet has closed. */
    fun consumePublished() {
        published.value = false
        photoUploadFailed.value = false
    }
}

/** Bundles the create-listing sub-state so the main combine stays within its arity limit. */
private data class PublishState(
    val publishing: Boolean,
    val gate: AppError?,
    val done: Boolean,
    val photoWarning: Boolean,
)

/** Snapshot of the browse tab's client-side filters (condition chips, sort, search, price band). */
private data class MarketplaceFilters(
    val condition: ItemCondition?,
    val category: MarketplaceCategory?,
    val sort: MarketplaceSort,
    val search: String,
    val priceBand: MarketplacePriceBand,
)

// Fallbacks MUST be Long literals: `?: 0` infers Comparable<*> mixing boxed Integer and Long, which
// throws ClassCastException the moment both appear in one sort — e.g. a just-published item whose
// pending server timestamp makes createdAtMillis null (crashed on every listing publish).
private val MarketplaceSort.comparator: Comparator<MarketplaceItem>
    get() = when (this) {
        MarketplaceSort.Newest -> compareByDescending { it.createdAtMillis ?: 0L }
        MarketplaceSort.PriceLow -> compareBy { if (it.isFree) 0L else it.priceCents ?: Long.MAX_VALUE }
        MarketplaceSort.PriceHigh -> compareByDescending { it.priceCents ?: 0L }
    }

private fun MarketplaceItem.matchesSearch(query: String): Boolean {
    val q = query.trim()
    return q.isEmpty() || title.contains(q, ignoreCase = true) || description.contains(q, ignoreCase = true)
}

/** Free listings count as $0; "price on request" (no price, not free) only shows under [MarketplacePriceBand.Any]. */
private fun MarketplaceItem.matchesPriceBand(band: MarketplacePriceBand): Boolean {
    if (band == MarketplacePriceBand.Any) return true
    if (band == MarketplacePriceBand.Free) return isFree
    val cents = if (isFree) 0L else priceCents ?: return false
    return when (band) {
        MarketplacePriceBand.Under50 -> cents < 50_00
        MarketplacePriceBand.From50To200 -> cents in 50_00..200_00
        MarketplacePriceBand.Over200 -> cents > 200_00
        MarketplacePriceBand.Any, MarketplacePriceBand.Free -> true
    }
}

/** Report lifecycle for a listing (mirrors the DS `ReportStatus`, with a sign-in gate for guests). */
enum class ItemReportStatus { Idle, Sending, Sent, SignInRequired, Failed }

data class MarketplaceItemDetailUiState(
    val loading: Boolean = true,
    val item: MarketplaceItem? = null,
    /** The seller's other active listings ("More from this seller"); empty hides the strip. */
    val sellerItems: List<MarketplaceItem> = emptyList(),
    val error: Boolean = false,
    val reportStatus: ItemReportStatus = ItemReportStatus.Idle,
    /** True when the signed-in user is the seller — surfaces seller controls (edit, mark sold). */
    val isSeller: Boolean = false,
    val markingSold: Boolean = false,
    val markSoldFailed: Boolean = false,
    val deleting: Boolean = false,
    val deleteFailed: Boolean = false,
    /** Flips true once after a successful delete so the screen pops back. */
    val deleted: Boolean = false,
    val savingEdit: Boolean = false,
    val editFailed: Boolean = false,
    /** Flips true once after a successful edit so the sheet can close. */
    val editSaved: Boolean = false,
    /** True when the viewer has bookmarked this listing. */
    val isSaved: Boolean = false,
    val addingPhoto: Boolean = false,
    val photoFailed: Boolean = false,
    /** In-flight ensure of the buyer↔seller thread (Message seller button spinner). */
    val openingConversation: Boolean = false,
    /** Set once when the thread is ready — the route navigates to it, then consumes. */
    val openConversationId: String? = null,
    /** Guest tapped Message seller — the route sends them to sign-in, then consumes. */
    val messageSellerSignInRequired: Boolean = false,
    val messageSellerFailed: Boolean = false,
)

class MarketplaceItemDetailViewModel(
    private val repository: MarketplaceRepository,
    private val reportRepository: ReportRepository,
    private val savedController: SavedItemsController,
    private val imagePicker: ImagePickController,
    private val analytics: AnalyticsTracker,
    private val chatRepository: ChatRepository,
    private val itemId: String,
) : ViewModel() {
    private val mutableState = MutableStateFlow(MarketplaceItemDetailUiState())
    val state: StateFlow<MarketplaceItemDetailUiState> = mutableState

    init {
        load()
        viewModelScope.launch {
            savedController.saved.collect { ids ->
                mutableState.value = mutableState.value.copy(isSaved = itemId in ids)
            }
        }
    }

    fun toggleSave() = savedController.toggle(itemId)

    /** Opens (creating on first contact) the buyer's thread with the seller about this listing. */
    fun messageSeller() {
        val current = mutableState.value
        val item = current.item ?: return
        if (current.isSeller || current.openingConversation) return
        viewModelScope.launch {
            mutableState.value = mutableState.value.copy(openingConversation = true, messageSellerFailed = false)
            when (val result = chatRepository.ensureItemConversation(item)) {
                is DataResult.Success -> mutableState.value = mutableState.value.copy(
                    openingConversation = false,
                    openConversationId = result.data,
                )
                is DataResult.Failure -> mutableState.value = mutableState.value.copy(
                    openingConversation = false,
                    messageSellerSignInRequired = result.error is AppError.Unauthorized,
                    messageSellerFailed = result.error !is AppError.Unauthorized,
                )
            }
        }
    }

    fun consumeConversation() {
        mutableState.value = mutableState.value.copy(
            openConversationId = null,
            messageSellerSignInRequired = false,
        )
    }

    fun retry() {
        mutableState.value = MarketplaceItemDetailUiState(isSaved = mutableState.value.isSaved)
        load()
    }

    /** Seller edits their own listing; on success the local item reflects the change and the sheet closes. */
    fun updateListing(
        title: String,
        description: String,
        priceCents: Long?,
        isFree: Boolean,
        condition: ItemCondition,
        category: MarketplaceCategory,
        city: String,
    ) {
        val current = mutableState.value
        if (current.savingEdit || current.item == null || !current.isSeller || title.isBlank()) return
        viewModelScope.launch {
            mutableState.value = current.copy(savingEdit = true, editFailed = false, editSaved = false)
            val result = repository.updateItem(itemId, title, description, priceCents, isFree, condition, category, city)
            mutableState.value = when (result) {
                is DataResult.Success -> mutableState.value.copy(
                    savingEdit = false,
                    editSaved = true,
                    item = mutableState.value.item?.copy(
                        title = title.trim(),
                        description = description.trim(),
                        priceCents = if (isFree) null else priceCents,
                        isFree = isFree,
                        condition = condition,
                        category = category,
                        city = city.trim(),
                    ),
                )
                is DataResult.Failure -> mutableState.value.copy(savingEdit = false, editFailed = true)
            }
        }
    }

    fun consumeEditSaved() {
        mutableState.value = mutableState.value.copy(editSaved = false)
    }

    /**
     * Opens the platform image picker; on a pick, uploads it to the listing and reloads so the new
     * photo shows. Seller-only (the repository/rules enforce ownership).
     */
    fun addPhoto() {
        val current = mutableState.value
        if (current.addingPhoto || current.item == null || !current.isSeller) return
        viewModelScope.launch {
            val image = imagePicker.pick() ?: return@launch
            mutableState.value = mutableState.value.copy(addingPhoto = true, photoFailed = false)
            when (repository.addPhoto(itemId, image)) {
                is DataResult.Success -> {
                    when (val reloaded = repository.getItem(itemId)) {
                        is DataResult.Success -> mutableState.value =
                            mutableState.value.copy(addingPhoto = false, item = reloaded.data)
                        is DataResult.Failure -> mutableState.value =
                            mutableState.value.copy(addingPhoto = false)
                    }
                }
                is DataResult.Failure -> mutableState.value =
                    mutableState.value.copy(addingPhoto = false, photoFailed = true)
            }
        }
    }

    /** Seller marks their own listing sold; on success the local item flips to [ItemStatus.Sold]. */
    fun markSold() {
        val current = mutableState.value
        if (current.markingSold || current.item == null || !current.isSeller) return
        viewModelScope.launch {
            mutableState.value = current.copy(markingSold = true, markSoldFailed = false)
            mutableState.value = when (repository.markSold(itemId)) {
                is DataResult.Success -> mutableState.value.copy(
                    markingSold = false,
                    item = mutableState.value.item?.copy(status = diana.soleil.design.iraniancanadienbazzar.core.model.ItemStatus.Sold),
                )
                is DataResult.Failure -> mutableState.value.copy(markingSold = false, markSoldFailed = true)
            }
        }
    }

    /** Seller permanently deletes their own listing; [ItemDetailUiState.deleted] pops the screen. */
    fun delete() {
        val current = mutableState.value
        if (current.deleting || current.item == null || !current.isSeller) return
        viewModelScope.launch {
            mutableState.value = current.copy(deleting = true, deleteFailed = false)
            mutableState.value = when (repository.deleteItem(itemId)) {
                is DataResult.Success -> mutableState.value.copy(deleting = false, deleted = true)
                is DataResult.Failure -> mutableState.value.copy(deleting = false, deleteFailed = true)
            }
        }
    }

    /** Files a moderation report against this listing; guests trip [ItemReportStatus.SignInRequired]. */
    fun report(reason: String) {
        if (mutableState.value.reportStatus == ItemReportStatus.Sending) return
        viewModelScope.launch {
            mutableState.value = mutableState.value.copy(reportStatus = ItemReportStatus.Sending)
            val result = reportRepository.fileReport("marketplace_item", itemId, reason)
            mutableState.value = mutableState.value.copy(
                reportStatus = when (result) {
                    is DataResult.Success -> ItemReportStatus.Sent
                    is DataResult.Failure ->
                        if (result.error is diana.soleil.design.iraniancanadienbazzar.core.common.AppError.Unauthorized)
                            ItemReportStatus.SignInRequired
                        else ItemReportStatus.Failed
                },
            )
        }
    }

    fun resetReport() {
        mutableState.value = mutableState.value.copy(reportStatus = ItemReportStatus.Idle)
    }

    private fun load() {
        viewModelScope.launch {
            val current = mutableState.value.reportStatus
            // isSaved is owned by the savedController collector in init; the saved StateFlow won't
            // re-emit an unchanged set, so a full state replacement must carry it forward or the
            // bookmark renders unsaved until the user toggles something.
            val saved = mutableState.value.isSaved
            mutableState.value = when (val result = repository.getItem(itemId)) {
                is DataResult.Success -> {
                    analytics.track(
                        AnalyticsEvents.MARKETPLACE_ITEM_VIEW,
                        buildMap {
                            put(AnalyticsParams.ITEM_ID, itemId)
                            result.data.metroId?.let { put(AnalyticsParams.METRO_ID, it) }
                        },
                    )
                    loadSellerItems(result.data.sellerUid)
                    MarketplaceItemDetailUiState(
                        loading = false,
                        item = result.data,
                        reportStatus = current,
                        isSaved = saved,
                        isSeller = repository.currentUid != null && repository.currentUid == result.data.sellerUid,
                    )
                }
                is DataResult.Failure ->
                    MarketplaceItemDetailUiState(loading = false, error = true, reportStatus = current, isSaved = saved)
            }
        }
    }

    /** Best-effort "More from this seller" strip — failures just leave the section hidden. */
    private fun loadSellerItems(sellerUid: String) {
        viewModelScope.launch {
            val result = repository.getActiveItemsBySeller(sellerUid, excludeItemId = itemId)
            if (result is DataResult.Success) {
                mutableState.value = mutableState.value.copy(sellerItems = result.data)
            }
        }
    }
}

/** Hero + two compact rows (Stitch 66b7e10b shows one hero and one row; a third keeps the block lively). */
private const val HOT_DEALS_COUNT = 3

/** Directory categories that read as "shops" on a marketplace tab (seed ids, see docs/DATA_MODEL.md). */
private val SHOP_CATEGORY_IDS = listOf("shopping", "grocery_specialty_food")
private const val SHOPS_RAIL_COUNT = 8
